import { useEffect, useRef } from "react";
import { trackScrollDepth } from "../utils/analytics";

/**
 * Hook for automatic scroll depth tracking
 * Tracks when user scrolls to 25%, 50%, 75%, and 100% of the page
 *
 * @param {Object} options - Configuration options
 * @returns {void}
 *
 * @example
 * useScrollTracking(); // Track scroll depth on current page
 */
export const useScrollTracking = (options = {}) => {
  const {
    milestones = [25, 50, 75, 100], // Scroll depth percentages to track
    trackPixels = false, // Also track pixel-based milestones
    pixelMilestones = [500, 1000, 2000, 3000], // Pixel milestones
    enabled = true, // Enable/disable tracking
  } = options;

  if (!enabled) return null;

  const trackedMilestonesRef = useRef(new Set());
  const maxScrollRef = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollPercentage = Math.round(
        (scrollTop / (documentHeight - windowHeight)) * 100,
      );

      // Track percentage-based milestones
      milestones.forEach((milestone) => {
        if (
          scrollPercentage >= milestone &&
          !trackedMilestonesRef.current.has(`percent_${milestone}`)
        ) {
          trackedMilestonesRef.current.add(`percent_${milestone}`);
          trackScrollDepth(milestone, "percentage");
        }
      });

      // Track pixel-based milestones
      if (trackPixels) {
        const currentScroll = scrollTop;
        maxScrollRef.current = Math.max(maxScrollRef.current, currentScroll);

        pixelMilestones.forEach((milestone) => {
          if (
            currentScroll >= milestone &&
            !trackedMilestonesRef.current.has(`pixel_${milestone}`)
          ) {
            trackedMilestonesRef.current.add(`pixel_${milestone}`);
            trackScrollDepth(milestone, "pixels");
          }
        });
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [milestones, trackPixels, pixelMilestones]);

  return null;
};

